import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { XIcon, PlayIcon } from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import { Dimensions, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';

import { InquiryGuidelinesSection } from '@/components/InquiryGuidelines';
import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Box } from '@/components/ui/box';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { DEMO_VIDEO_URL } from '@/constants/links';
import { useInquiryDemoModal } from '@/hooks/useInquiryDemoModal';
import { getCurrentUserAuthenticatedQueryOptions } from '@/services/getCurrentUserScoped';
import { userIdAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';

export interface InquiryDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'auto' | 'manual';
}

/**
 * Inquiry Demo Modal Component
 *
 * Displays a step-by-step tutorial video for users. Supports both auto-show
 * (for new users) and manual open (from popover clicks) modes.
 *
 * Features:
 * - Vimeo embedded video player
 * - Auto mode: storage tracking, dismissal buttons, analytics
 * - Manual mode: simple viewer, no storage side-effects
 * - Responsive design with different sizes for web/mobile
 * - Web: native HTML5 video, Mobile: WebView for compatibility
 *
 * Video Handling:
 * - Uses Vimeo player embed for reliable video delivery
 * - Web: HTML5 video element with controls and event handlers
 * - Mobile: WebView with loading states and message handling
 * - Built-in Vimeo player error handling
 *
 * Analytics Events:
 * - INQUIRY_DEMO_OPENED: when modal becomes visible
 * - INQUIRY_DEMO_ACCESSED: when video starts playing
 * - INQUIRY_DEMO_CLOSED: when modal closes (tracks close method and video state)
 *
 * Storage Behavior:
 * - Auto mode: tracks dismissal and deferral in user storage
 * - Manual mode: no storage side-effects, pure viewer
 * - Dismissal buttons only shown in auto mode
 */
export function InquiryDemoModal({ isOpen, onClose, mode }: InquiryDemoModalProps) {
  const userId = useAtomValue(userIdAtom);

  const { data: currentUser } = useQuery(getCurrentUserAuthenticatedQueryOptions(userId));

  const { markShown, markShowLater } = useInquiryDemoModal(currentUser?.id?.toString());

  const [webViewLoading, setWebViewLoading] = useState(true);

  const [modalOpenTime, setModalOpenTime] = useState<number | null>(null);

  const trackModalClosed = useCallback(
    (closeMethod: 'close_button' | 'click_outside' | 'remind_me_later' | 'continue_to_primum') => {
      if (modalOpenTime && currentUser?.id) {
        const duration = Date.now() - modalOpenTime;

        segmentClient.track(ANALYTICS_EVENTS.INQUIRY_DEMO_CLOSED, {
          user_id: currentUser.id,
          user_email: currentUser.email,
          user_type: currentUser.userType,
          trigger_type: mode === 'auto' ? 'new_user' : 'demo_link',
          video_source: 'vimeo',
          duration_ms: duration,
          close_method: closeMethod,
        });
      }
    },
    [modalOpenTime, currentUser, mode]
  );

  const handleDismiss = useCallback(
    async (
      dismissalType: 'remind_later' | 'dont_show',
      closeMethod: 'close_button' | 'click_outside' | 'remind_me_later' | 'continue_to_primum'
    ) => {
      trackModalClosed(closeMethod);

      if (mode === 'auto' && currentUser?.id) {
        if (dismissalType === 'remind_later') {
          await markShowLater();
        } else {
          await markShown();
        }
      }

      segmentClient.track(ANALYTICS_EVENTS.INQUIRY_DEMO_DISMISSED, {
        user_id: currentUser?.id,
        user_email: currentUser?.email,
        user_type: currentUser?.userType,
        trigger_type: mode === 'auto' ? 'new_user' : 'demo_link',
        video_source: 'vimeo',
        dismissal_type: dismissalType,
      });

      onClose();
    },
    [mode, currentUser, onClose, markShown, markShowLater, trackModalClosed]
  );

  const handleModalClose = useCallback(() => {
    handleDismiss('remind_later', 'close_button');
  }, [handleDismiss]);

  const handleBackdropClose = useCallback(() => {
    handleDismiss('remind_later', 'click_outside');
  }, [handleDismiss]);

  const handleRemindLater = useCallback(() => {
    handleDismiss('remind_later', 'remind_me_later');
  }, [handleDismiss]);

  const handleDontShowAgain = useCallback(() => {
    handleDismiss('dont_show', 'continue_to_primum');
  }, [handleDismiss]);

  useSafeAreaInsets();

  useEffect(() => {
    if (isOpen && currentUser?.id) {
      setModalOpenTime(Date.now());

      segmentClient.track(ANALYTICS_EVENTS.INQUIRY_DEMO_OPENED, {
        user_id: currentUser.id,
        user_email: currentUser.email,
        user_type: currentUser.userType,
        trigger_type: mode === 'auto' ? 'new_user' : 'demo_link',
        video_source: 'vimeo',
      });
    } else {
      setModalOpenTime(null);
    }
  }, [isOpen, currentUser, mode]);

  const renderVideoContent = () => {
    const videoUrl = DEMO_VIDEO_URL;

    if (isWeb) {
      return (
        <VStack className="relative z-0 -mx-6 w-screen overflow-hidden sm:mx-0 sm:w-full sm:rounded-lg">
          <iframe
            src={videoUrl}
            className="block w-full"
            style={{ aspectRatio: 16 / 9 }}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Inquiry Demo Video"
          />
        </VStack>
      );
    }

    return (
      <VStack className="relative z-0 -mx-6 w-screen overflow-hidden sm:mx-0 sm:w-full sm:rounded-lg">
        {webViewLoading && (
          <VStack className="absolute inset-0 z-10 items-center justify-center bg-background-100">
            <Icon as={PlayIcon} className="mb-2 h-8 w-8 text-primary-400" />
            <Text className="text-sm text-typography-500">Loading video...</Text>
          </VStack>
        )}

        <WebView
          source={{ uri: videoUrl }}
          className="w-full"
          style={{
            aspectRatio: 16 / 9,
            backgroundColor: 'transparent',
            zIndex: 0,
            pointerEvents: 'box-none',
          }}
          containerStyle={{ zIndex: 0 }}
          onLoadStart={() => setWebViewLoading(true)}
          onLoadEnd={() => setWebViewLoading(false)}
          originWhitelist={['*']}
          setSupportMultipleWindows={false}
          allowsFullscreenVideo
          onMessage={() => {
            // Vimeo embed doesn't send messages - placeholder for future use
          }}
          onError={() => {
            setWebViewLoading(false);

            console.warn('Video failed to load, but Vimeo should be reliable');
          }}
        />
      </VStack>
    );
  };

  // Constrain native actionsheet height to show backdrop (92% of screen)
  const sheetMaxHeight = Math.round(Dimensions.get('window').height * 0.92);

  if (isWeb) {
    return (
      <Modal isOpen={isOpen} size={isWeb ? 'lg' : 'full'} onClose={handleBackdropClose}>
        <ModalBackdrop />
        <ModalContent
          className={
            isWeb
              ? 'relative max-h-[80vh] overflow-hidden bg-background-0 p-0'
              : 'relative m-0 h-full w-full bg-background-0 p-0'
          }>
          <VStack className="w-full flex-1">
            <ModalHeader className="flex-row items-center justify-between px-6 pb-4 pt-12 sm:pt-6">
              {mode === 'auto' ? (
                <Heading size="h1-2xl" className="text-left font-bold text-primary-500">
                  👋 Welcome to Primum!
                </Heading>
              ) : (
                <Box className="flex-1" />
              )}
              <ModalCloseButton onPress={handleModalClose} className="bg-transparent">
                <Icon as={XIcon} className="h-7 w-7 text-typography-600 sm:h-6 sm:w-6" />
              </ModalCloseButton>
            </ModalHeader>

            <ModalBody className="flex-1 px-6">
              <VStack className="w-full sm:mx-auto sm:h-full sm:max-w-2xl">
                {renderVideoContent()}
                <Box className="mt-6">
                  <Text className="text-left font-semibold text-lg text-typography-950">
                    Submitting an Inquiry: Step-by-Step
                  </Text>
                  <Text className="mt-2 font-normal text-sm leading-[21px] text-typography-950">
                    Primum CEO Erica Conroy, Ph.D., walks you through the step-by-step process of
                    drafting an expert inquiry in the app.
                  </Text>
                </Box>

                <Box className="mt-8 pb-6">
                  <InquiryGuidelinesSection context="demo_modal" />
                </Box>
              </VStack>
            </ModalBody>

            {mode === 'auto' && (
              <VStack className="border-t border-background-200 px-6 pb-4 pt-3">
                <HStack space="sm" className="w-full">
                  <Button
                    variant="outline"
                    onPress={handleRemindLater}
                    className="flex-1"
                    size="md">
                    <ButtonText>{isWeb ? 'Remind Me Later' : 'Later'}</ButtonText>
                  </Button>
                  <Button onPress={handleDontShowAgain} className="flex-1" size="md">
                    <ButtonText>{isWeb ? 'Continue to Primum' : 'Continue'}</ButtonText>
                  </Button>
                </HStack>
              </VStack>
            )}
          </VStack>
        </ModalContent>
      </Modal>
    );
  }

  // Native: use Actionsheet for full-height, scrollable content
  return (
    <Actionsheet isOpen={isOpen} onClose={handleBackdropClose}>
      <ActionsheetBackdrop />
      <ActionsheetContent
        className="w-full rounded-t-2xl p-0"
        style={{ maxHeight: sheetMaxHeight }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <ActionsheetScrollView contentContainerClassName="px-6 pb-16">
          <HStack className="w-full items-center justify-between border-b border-background-200 py-3">
            {mode === 'auto' ? (
              <Heading size="h1-2xl" className="text-left font-bold text-primary-500">
                👋 Welcome to Primum!
              </Heading>
            ) : (
              <Box className="flex-1" />
            )}
            <Pressable
              onPress={handleModalClose}
              accessibilityRole="button"
              accessibilityLabel="Close"
              className="-mr-2 shrink-0 p-2">
              <Icon as={XIcon} className="h-7 w-7 text-typography-600" />
            </Pressable>
          </HStack>

          <VStack className="w-full sm:mx-auto sm:max-w-2xl">
            {renderVideoContent()}
            <Box className="mt-6">
              <Text className="text-left font-semibold text-lg text-typography-950">
                Submitting an Inquiry: Step-by-Step
              </Text>
              <Text className="mt-2 font-normal text-sm leading-[21px] text-typography-950">
                Primum CEO Erica Conroy, Ph.D., walks you through the step-by-step process of
                drafting an expert inquiry in the app.
              </Text>
            </Box>

            <Box className="mt-8 pb-6">
              <InquiryGuidelinesSection context="demo_modal" />
            </Box>
          </VStack>
        </ActionsheetScrollView>
      </ActionsheetContent>
    </Actionsheet>
  );
}
